import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import AllOrderTable from '../../../components/SellerDashboard/OrderTable/AllOrder';
import adminModule from '../../../api/modules/admin.module';
import { formatCurrency } from '../../../utils/common';
import { AdminDashboardView } from './AdminDashboard.view';

/**
 * Admin dashboard, the container. Owns the one query; everything visual lives
 * in `AdminDashboard.view.jsx`, which is pure, so the state explorer can mount
 * the real screen without the API running.
 *
 * The query, its key and its error contract are unchanged from before the
 * conversion. What changed is that the error is now RENDERED instead of being
 * written to `console.error` and then ignored while the tiles kept showing 0,
 * which is how a failed fetch used to look identical to an empty marketplace.
 *
 * `AdminDashboard.css` is gone: nothing imports it and no class in it survives.
 */
const AdminDashboard = () => {
  const {
    data: dashboardStats,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['adminDashboardStats'],
    queryFn: async () => {
      const result = await adminModule.getAdminDashboardStats();
      if (result.success) return result.data;
      throw new Error(result.error);
    },
  });

  // Currency formatting is the container's job so the view stays free of the
  // Rand rule and cannot accidentally render a raw number beside a formatted
  // one. `formatCurrency` already prefixes R and never a dollar sign.
  const stats = useMemo(() => {
    if (!dashboardStats) return null;
    return {
      ...dashboardStats,
      formattedRequested: formatCurrency(Number(dashboardStats.total_requested || 0)),
    };
  }, [dashboardStats]);

  return (
    <AdminDashboardView
      stats={stats}
      loading={isLoading}
      error={error}
      onRetry={refetch}
      ordersTable={<AllOrderTable />}
    />
  );
};

export default AdminDashboard;
